/**
 * LanguageSwitcher Component v3.0 - PLANIVIO REBRANDING
 * 
 * Uses position: fixed to avoid overflow issues
 * 
 * Props:
 * - variant: 'dropdown' | 'inline' (default: 'dropdown')
 * - showLabel: boolean (default: true)
 * 
 * ✅ v3.0: Planivio Rebranding
 *          - Farben: Orange (#F97316) → Teal (#4A90A4)
 * 
 * @version 3.0.0 - Planivio Rebranding
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../context/I18nContext';

function LanguageSwitcher({ variant = 'dropdown', showLabel = true }) {
  const { language, setLanguage, currentLanguage, languages } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen]);
  
  const changeLanguage = (langCode) => {
    setLanguage(langCode);
    setIsOpen(false);
  };
  
  // ✅ v3: Planivio Styles
  const styles = {
    container: { position: 'relative', display: 'inline-block' },
    trigger: {
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 12px', background: '#FFFFFF',
      // ✅ v3: Planivio Teal border
      border: '2px solid #B8D4DC', borderRadius: '10px',
      cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
      color: '#6B7280', transition: 'all 0.2s ease',
      minWidth: showLabel ? '120px' : '50px'
    },
    // ✅ v3: Planivio Teal open state
    triggerOpen: { borderColor: '#4A90A4', boxShadow: '0 4px 12px rgba(74, 144, 164, 0.2)' },
    arrow: { marginLeft: 'auto', transition: 'transform 0.2s ease', fontSize: '0.7rem', color: '#9CA3AF' },
    arrowOpen: { transform: 'rotate(180deg)' },
    dropdown: {
      position: 'fixed',
      top: dropdownPosition.top,
      left: dropdownPosition.left,
      background: '#FFFFFF',
      // ✅ v3: Planivio Teal border
      border: '2px solid #B8D4DC',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
      zIndex: 99999,
      minWidth: '150px',
      overflow: 'visible'
    },
    option: {
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '12px 16px', cursor: 'pointer', fontSize: '0.9rem',
      fontWeight: 500, color: '#374151', transition: 'all 0.15s ease',
      borderBottom: '1px solid #F3F4F6', background: '#FFFFFF'
    },
    optionLast: { borderBottom: 'none', borderRadius: '0 0 10px 10px' },
    optionFirst: { borderRadius: '10px 10px 0 0' },
    // ✅ v3: Planivio Teal active option
    optionActive: {
      background: 'linear-gradient(135deg, #E8F4F8 0%, #D4E8EC 100%)',
      color: '#4A90A4', fontWeight: 600
    },
    // ✅ v3: Planivio Teal checkmark
    checkmark: { marginLeft: 'auto', color: '#4A90A4', fontSize: '0.9rem' }
  };
  
  // Inline variant - simple buttons
  if (variant === 'inline') {
    return (
      <div style={{ display: 'flex', gap: '6px' }}>
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            style={{
              padding: '6px 12px', 
              // ✅ v3: Planivio Teal gradient for active
              background: language === lang.code ? 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)' : '#F3F4F6',
              border: language === lang.code ? '2px solid #4A90A4' : '2px solid transparent', 
              borderRadius: '8px',
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
              color: language === lang.code ? 'white' : '#6B7280',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}
            title={lang.name}
          >
            <span>{lang.flag}</span>
            {showLabel && <span>{lang.code.toUpperCase()}</span>}
          </button>
        ))}
      </div>
    );
  }
  
  // Dropdown variant (default)
  return (
    <div style={styles.container}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        style={{ ...styles.trigger, ...(isOpen ? styles.triggerOpen : {}) }}
      >
        <span>{currentLanguage.flag}</span>
        {showLabel && <span>{currentLanguage.name}</span>}
        <span style={{ ...styles.arrow, ...(isOpen ? styles.arrowOpen : {}) }}>▼</span>
      </button>
      
      {isOpen && (
        <div ref={dropdownRef} style={styles.dropdown}>
          {languages.map((lang, index) => (
            <div
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              style={{
                ...styles.option,
                ...(index === 0 ? styles.optionFirst : {}),
                ...(index === languages.length - 1 ? styles.optionLast : {}),
                ...(language === lang.code ? styles.optionActive : {})
              }}
              onMouseEnter={(e) => {
                // ✅ v3: Planivio Teal hover
                if (language !== lang.code) e.currentTarget.style.background = '#E8F4F8';
              }}
              onMouseLeave={(e) => {
                if (language !== lang.code) e.currentTarget.style.background = '#FFFFFF';
              }}
            >
              <span style={{ fontSize: '1.2rem' }}>{lang.flag}</span>
              <span>{lang.name}</span>
              {language === lang.code && <span style={styles.checkmark}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;